import React, { useState, useEffect } from 'react';
import { Plus, Play, MoreVertical, Lock, X, Music, PencilIcon, ShareIcon, TrashIcon, FileText } from 'lucide-react';
import Sidebar from '../../Sidebar/Sidebar';
import Navbar from '../../Navbars/UserNavbar';
import { DashboardApi } from '../../../apis/DashboardApi';
import { AxiosResponse } from 'axios';
import { IoInformation } from 'react-icons/io5';
import { NavLink } from 'react-router-dom';
import MediaGridWithPagination from './MediaGridWithPagination';

interface Tab {
    id: string;
    label: string;
}

interface Media {
    id: number;
    file_name: string;
    file_type: string;
    temporaryUrl: string;
    is_legacy: number;
    created_at?: string;
    file_size?: string;
    recipients?: any[];
    handleDelete?: () => void;
    handleRename?: () => void;
    handleShare?: () => void;
}

interface MediaResponse {
    status: string;
    data: {
        files: {
            data: Media[];
            current_page: number;
            last_page: number;
        };
    };
}

interface MediaModalProps {
    media: Media | null;
    onClose: () => void;
}

const MediaModal: React.FC<MediaModalProps> = ({ media, onClose }) => {
    if (!media) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="relative bg-white rounded-lg max-w-4xl w-full mx-4" onClick={e => e.stopPropagation()}>
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 z-10"
                >
                    <X className="w-6 h-6" />
                </button>

                <div className="p-4">
                    {media.file_type === 'video' && (
                        <div className="aspect-video">
                            <video
                                src={media.temporaryUrl}
                                controls
                                className="w-full h-full rounded-lg"
                            >
                                Your browser does not support video playback.
                            </video>
                        </div>
                    )}

                    {media.file_type === 'image' && (
                        <div className="relative max-h-[80vh] overflow-hidden">
                            <img
                                src={media.temporaryUrl}
                                alt={media.file_name}
                                className="w-full h-full object-contain rounded-lg"
                            />
                        </div>
                    )}

                    {media.file_type === 'audio' && (
                        <div className="p-8">
                            <audio
                                src={media.temporaryUrl}
                                controls
                                className="w-full"
                            >
                                Your browser does not support audio playback.
                            </audio>
                        </div>
                    )}

                    {media.file_type === 'document' && (
                        <div className="p-2 pt-5">
                            <iframe
                                src={`https://docs.google.com/viewer?url=${encodeURIComponent(media.temporaryUrl)}&embedded=true`}
                                className="w-full max-h-[600px] border rounded-md"
                                frameBorder="0"
                            >
                                Your browser does not support viewing documents.
                            </iframe>
                        </div>
                    )}

                    <div className="mt-4 flex justify-between items-center">
                        <h3 className="text-lg font-medium">{media.file_name}</h3>
                        {media.is_legacy === 1 && (
                            <div className="flex items-center gap-2">
                                <Lock className="w-4 h-4 text-gray-500" />
                                <span className="text-sm text-gray-500">legacy</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

interface DropdownPosition {
    x: number;
    y: number;
}

interface MediaDropdownProps {
    isOpen: boolean;
    onClose: (e: any) => void;
    position: DropdownPosition;
}

const MediaDropdown: React.FC<MediaDropdownProps> = ({ isOpen, onClose, position }) => (
    <div
        className={`z-0 bg-white rounded-lg shadow-lg py-1 ${isOpen ? 'block' : 'hidden'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
            top: "100%",  // Position below the button
            right: 0,     // Align to right
            minWidth: "160px"
        }}
    >
        <div className='flex justify-end'><X className='text-orange-500' onClick={(e) => {
            e.stopPropagation();
            onClose(e);
        }} /></div>
        <button className={`w-full px-4 py-2.5 text-sm text-left transition-colors flex items-center gap-2 hover:bg-orange-50 hover:text-orange-500 text-orange-500`}>
            <ShareIcon className="w-4 h-4" />
            Share
        </button>
        <hr className="my-1 border-gray-100" />
        <button className={`w-full px-4 py-2.5 text-sm text-left transition-colors flex items-center gap-2 hover:bg-red-50 hover:text-red-600 text-red-500`}>
            <TrashIcon className="w-4 h-4" />
            Delete
        </button>
    </div>
);

const Legacy = () => {
    const [media, setMedia] = useState<Media[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('legacy');
    const [activeFilter, setActiveFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [selectedMedia, setSelectedMedia] = useState<Media | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);

    const mainTabs: Tab[] = [
        { id: 'legacy', label: 'Legacy' }
    ];

    const filters: Tab[] = [
        { id: 'all', label: 'All' },
        { id: 'video', label: 'Video' },
        { id: 'image', label: 'Image' },
        { id: 'audio', label: 'Audio' },
        { id: 'document', label: 'Document' }
    ];

    const fetchLegacyMedia = (page: number = 1) => {
        setLoading(true);
        DashboardApi.getLegacyMedia(page)
            .then((response: any) => {
                if (response?.data?.data?.files) {
                    setMedia(response.data.data.files.data || []);
                    setCurrentPage(response.data.data.files.current_page);
                    setTotalPages(response.data.data.files.last_page);
                }
                setLoading(false);
            })
            .catch(function (error) {
                console.error('Error fetching legacy media:', error);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchLegacyMedia(1);
    }, []);

    const handleMediaClick = (media: Media) => {
        setSelectedMedia(media);
    };

    const handlePageChange = (page: number) => {
        fetchLegacyMedia(page);
    };

    const getMediaCard = (item: Media) => {
        const commonClasses = "relative rounded-lg overflow-hidden bg-gray-100 h-[300px] w-full";
        const bottomBarClasses = "absolute bottom-0 left-0 right-0 flex justify-between items-center p-3 bg-gradient-to-t from-black/50 to-transparent";
        const privateTagClasses = "absolute top-2 right-2 flex items-center gap-2 bg-black/30 px-2 py-1 rounded";

        switch (item.file_type) {
            case 'video':
                return (
                    <div className={commonClasses}>
                        <video src={item.temporaryUrl} className="w-full h-full object-cover" />
                        <div onClick={() => handleMediaClick(item)} className="absolute inset-0 flex items-center justify-center">
                            <button className="bg-black/50 rounded-full p-3 hover:bg-black/70">
                                <Play className="w-6 h-6 text-white" />
                            </button>
                        </div>
                        {item.is_legacy === 1 && (
                            <div className={privateTagClasses}>
                                <span className="text-white text-sm">Legacy</span>
                            </div>
                        )}
                        <div className={bottomBarClasses}>
                            <span className="text-white text-sm truncate">{item.file_name}</span>
                        </div>
                    </div>
                );

            case 'image':
                return (
                    <div className={commonClasses}>
                        <img onClick={() => handleMediaClick(item)} src={item.temporaryUrl} alt={item.file_name} className="w-full h-full object-cover" />
                        {item.is_legacy === 1 && (
                            <div className={privateTagClasses}>
                                <Lock className="w-4 h-4 text-white" />
                                <span className="text-white text-sm">Legacy</span>
                            </div>
                        )}
                        <div className={bottomBarClasses}>
                            <span className="text-white text-sm truncate">{item.file_name}</span>
                        </div>
                    </div>
                );

            case 'audio':
                return (
                    <div onClick={() => handleMediaClick(item)} className={`${commonClasses} bg-white border flex flex-col justify-center items-center`}>
                        <Music className="w-16 h-16 text-gray-400 mb-4" />
                        <div className="w-3/4 h-2 bg-gray-200 rounded-full" />
                        {item.is_legacy === 1 && (
                            <div className={`${privateTagClasses} bg-gray-100`}>
                                <Lock className="w-4 h-4 text-gray-500" />
                            </div>
                        )}
                        <div className={bottomBarClasses}>
                            <span className="text-white text-sm truncate">{item.file_name}</span>
                        </div>
                    </div>
                );

            case 'document':
                return (
                    <div className={`${commonClasses} bg-white border flex flex-col justify-center items-center`}>
                        <FileText onClick={() => handleMediaClick(item)} className="w-16 h-16 text-gray-400 mb-4" />
                        <span className="text-sm text-gray-500 truncate">{item.file_name}</span>
                        {item.is_legacy === 1 && (
                            <div className={`${privateTagClasses} bg-gray-100`}>
                                <Lock className="w-4 h-4 text-gray-500" />
                                <span className="text-gray-400 text-sm">Legacy</span>
                            </div>
                        )}
                        <div className={bottomBarClasses}>
                            <span className="text-white text-sm truncate">{item.file_name}</span>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <div className="flex flex-col md:flex-row">
                <Sidebar />
                <main className="flex-1 p-6 relative md:ml-64">
                    <Navbar />
                    <div className="p-4 md:p-6">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 space-y-4 md:space-y-0">
                            <div className="flex space-x-6 overflow-x-auto pb-2 md:pb-0">
                                {mainTabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        className={`whitespace-nowrap pb-2 font-medium text-sm md:text-base border-b-2 cursor-pointer transition-colors
                                          ${activeTab === tab.id
                                            ? 'text-orange-500 border-orange-500'
                                            : 'text-gray-600 border-transparent hover:text-orange-500'}`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-2 mb-8">
                            {filters.map((filter) => (
                                <button
                                    key={filter.id}
                                    onClick={() => setActiveFilter(filter.id)}
                                    className={`px-6 py-2 rounded-full text-sm transition-colors
                                      ${activeFilter === filter.id
                                        ? 'bg-blue-700 text-white'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    {filter.label}
                                </button>
                            ))}
                        </div>

                        <MediaGridWithPagination
                            media={media}
                            loading={loading}
                            activeFilter={activeFilter}
                            getMediaCard={getMediaCard}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            isServerPagination={true}
                        />
                    </div>
                </main>

                {selectedMedia && (
                    <MediaModal
                        media={selectedMedia}
                        onClose={() => setSelectedMedia(null)}
                    />
                )}
            </div>
        </div>
    );
};

export default Legacy;